/**
 * Custom Hook: useOtCache
 * Spec: 003-optimized-ot-list
 * 
 * Hook para gestionar caché de lista de OTs en localStorage
 * con TTL de 20 minutos e invalidación por Socket.io
 */

import { useState, useCallback, useEffect } from 'react';
import { useSocketOt } from './useSocketOt';
import { useAuth } from './useAuth';

const CACHE_TTL = 20 * 60 * 1000; // 20 minutos en milisegundos
const CACHE_VERSION = '1.0';

// Serializa la combinación de filtros, página y perPage para usar como subclave
function serializeKey({ page, perPage, filters }) {
  // Ordena las claves de los filtros para evitar problemas de orden
  const orderedFilters = {};
  Object.keys(filters || {}).sort().forEach(k => {
    const v = filters[k];
    // Solo guarda valores primitivos (no objetos)
    orderedFilters[k] = typeof v === 'object' && v !== null ? v._id || '' : v;
  });
  return `${page}|${perPage}|${JSON.stringify(orderedFilters)}`;
}

/**
 * Hook para gestión de caché con localStorage y invalidación Socket.io
 * @returns {Object} { getCached, setCached, invalidate, pendingUpdates, clearPendingUpdates }
 */

export const useOtCache = () => {
  const { userData } = useAuth();
  const [pendingUpdates, setPendingUpdates] = useState(0);

  console.log('useOtCache inicializado para usuario:', userData);
  // Generar cache key con userId para seguridad (TASK-000A)
  const getCacheKey = useCallback(() => {
    if (!userData?._id) return null;
    return `ot_list_cache_${userData._id}`;
  }, [userData?._id]);

  /**
   * Obtener datos del cache si son válidos
   * @param {Object} filters - Filtros actuales para comparar
   * @returns {Object|null} Datos cacheados o null si expiró/no existe
   */
  // Nuevo: getCached devuelve el último dato guardado en caché, sin validar filtros/paginado
  const getCached = useCallback(() => {
    try {
      const cacheKey = getCacheKey();
      if (!cacheKey) return null;
      const cachedRaw = localStorage.getItem(cacheKey);
      if (!cachedRaw) return null;
      const cacheObj = JSON.parse(cachedRaw);
      if (!cacheObj || typeof cacheObj !== 'object') return null;
      // Buscar solo la subclave 'latest'
      const latest = cacheObj['latest'];
      if (!latest) return null;
      if (latest.version !== CACHE_VERSION) return null;
      const now = Date.now();
      if (now - latest.timestamp > CACHE_TTL) return null;
      return latest.data;
    } catch (error) {
      console.error('❌ Error leyendo caché:', error);
      return null;
    }
  }, [getCacheKey]);

  /**
   * Guardar datos en caché
   * @param {Object} data - Datos a cachear
   * @param {Object} filters - Filtros usados para obtener los datos
   */
  // Nuevo: setCached guarda en el objeto de caché bajo la subclave serializada
  const setCached = useCallback((data, params) => {
    try {

      console.log('Guardando datos en caché:', { data, params });
      const cacheKey = getCacheKey();

      console.log('Clave de caché generada:', cacheKey);
      if (!cacheKey) return;

      // Guardar SIEMPRE bajo una sola subclave fija
      const subKey = 'latest';
      const cacheObj = { [subKey]: {
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION
      }};

      console.log('Caché a guardar:', cacheObj, 'con clave:', cacheKey);
      localStorage.setItem(cacheKey, JSON.stringify(cacheObj));
      console.log('✅ Caché guardado exitosamente');
    } catch (error) {
      console.error('❌ Error guardando caché:', error);
      if (error.name === 'QuotaExceededError') {
        const cacheKey = getCacheKey();
        if (cacheKey) localStorage.removeItem(cacheKey);
      }
    }
  }, [getCacheKey]);

  /**
   * Invalidar caché (forzar recarga en próxima consulta)
   */
  const invalidate = useCallback(() => {
    const cacheKey = getCacheKey();
    if (!cacheKey) return;

    localStorage.removeItem(cacheKey);
    console.log('🗑️ Caché invalidado');
  }, [getCacheKey]);

  /**
   * Limpiar contador de actualizaciones pendientes
   */
  const clearPendingUpdates = useCallback(() => {
    setPendingUpdates(0);
  }, []);

  // Integración con Socket.io para invalidación automática
  useSocketOt(
    useCallback((updateInfo) => {
      console.log(`📡 Actualización de OT detectada (${updateInfo.accion}):`, updateInfo.numeroOrden);
      
      // Invalidar caché
      invalidate();
      
      // Incrementar contador de actualizaciones pendientes
      setPendingUpdates(prev => prev + 1);
    }, [invalidate])
  );

  return {
    getCached,
    setCached,
    invalidate,
    pendingUpdates,
    clearPendingUpdates
  };
};
